import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Timeline, TimelineLite, TimelineMax, TweenLite, TweenMax} from "gsap";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {TombOfNefertiti} from "../../TombOfNefertiti";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";

export interface IBoxConfig {
    backgroundTexture?: PIXI.Texture;
    title?: string;
    text?: string;
    infoSprite?: PIXI.Sprite;
}

export class SpritePageFlipper extends BaseView {
    protected _currentIndex: number = 0;
    protected _isAnimationsInit: boolean = false;
    // Page/box elements
    protected _pageSprites: PIXI.Container [] = [];
    protected _boxWrapperSprite: PIXI.Sprite;
    protected _boxConfigs: IBoxConfig [] = [];
    // Basic next-/prev-buttons
    protected _arrowNextSprite: PIXI.Sprite;
    protected _arrowPrevSprite: PIXI.Sprite;
    protected _nextButton: PIXI.Sprite;
    protected _previousButton: PIXI.Sprite;
    // Very game specific fluff
    protected _radioButtons: PIXI.Sprite [] = [];
    protected _radioButtonWrapper: PIXI.Sprite;
    // Transitions & Timelines
    protected _pageTransitionTimeline: Timeline | null;
    protected _pageFadeOutTween: TweenLite | null;
    // Slide show
    protected _slideShowTimeline: Timeline | null;
    protected _slideShowRunning: boolean = false;
    // Texts
    protected _titles: PIXI.Text [] = [];
    protected _paragraphs: PIXI.Text [] = [];

    constructor(arrowPrev?: PIXI.Sprite, arrowNext?: PIXI.Sprite, boxConfigs: IBoxConfig [] = []) {
        super();

        this._boxConfigs = boxConfigs;


        if (arrowPrev) {
            this._arrowPrevSprite = arrowPrev;
        }
        if (arrowNext) {
            this._arrowNextSprite = arrowNext;
        }
    }

    private _interactEnabled: boolean = false;

    public set interactEnabled(value: boolean) {
        if (this._interactEnabled !== value) {
            this._interactEnabled = value;
            this.buttonMode = this._interactEnabled;
        }
    }

    protected _slideShowEnabled: boolean = false;

    public set slideShowEnabled(value: boolean) {
        if (this._slideShowEnabled !== value) {
            this._slideShowEnabled = value;
        }
        if (!this._slideShowEnabled) {
            this.stopSlideShow();
        }
    }

    /**
     * To be able to call externally (e.g. from intro screen)
     */
    public forceInitAnimations(): void {
        if (!this._isAnimationsInit) {
            this.initAnimations();
        }
    }

    public startSlideShow(slideInterval: number = 5): void {
        const tl: TimelineLite = new TimelineMax({repeat: -1});

        if (this._slideShowTimeline && this._slideShowTimeline.isActive()) {
            this._slideShowTimeline.pause();
            this._slideShowTimeline.kill();
        }
        this._slideShowRunning = true;

        tl.add(() => {
            if (!this._slideShowEnabled && this._slideShowTimeline) {
                this.stopSlideShow();
            }
        });
        this._pageSprites.forEach((item:PIXI.Container,index:number) => {
            if(index===1){
                if(!TombOfNefertiti.api.gameClientConfiguration.showLowProbabilityGfx){
                    (<PIXI.Text> item.children[1]).text = Translation.translate("Respin with higher multiplier, each win may award 2 respins.");
                }
            }
            tl.add(() => {
                this.toggleNextPage();
            }, `+=${slideInterval}`);
        });

        this._slideShowTimeline = tl;
    }

    protected initAnimations() {
        if (!this._isAnimationsInit) {
            this._boxWrapperSprite = new PIXI.Sprite();
            this._boxWrapperSprite.scale.set(0.78);
            this._boxWrapperSprite.anchor.set(0.5);

            this.createPages();
            this.createNextButton();
            this.createPreviousButton();
            this.initBaseInteractAbles();

            if (this.createFluff) {
                this.createFluff();

                if (this.initFluffInteractAbles) {
                    this.initFluffInteractAbles();
                }
            }

            this.addChild(this._boxWrapperSprite);
            this._isAnimationsInit = true;
        }
    }

    protected getPageTransitionTimeline(fromPage: PIXI.Container, toPage: PIXI.Container): Timeline {
        const tl: TimelineLite = new TimelineLite();

        if (this._pageTransitionTimeline) {
            this._pageTransitionTimeline.pause();
            this._pageTransitionTimeline.kill();
        }
        if (this._pageFadeOutTween) {
            this._pageFadeOutTween.timeScale(5);
        }
        this._pageSprites.forEach((sprite) => {
            sprite.alpha = 0;
        });

        tl.add([
            this._pageFadeOutTween = TweenLite.to(fromPage, 1.6, {
                alpha: 0
            }),
            TweenLite.fromTo(toPage, 1.4, {
                alpha: 0
            }, {
                alpha: 1
            })
        ]);

        return this._pageTransitionTimeline = tl;
    }

    /**
     * Basic setup and positioning of pages and it's children, most likely to be
     * completely overriddn with game specific implementation.
     */
    protected createPages(): void {
        this._boxConfigs.forEach((box, index) => {
            if (box.backgroundTexture) { // TODO: This specific implementation only uses one bgTexture, and it's not really bg but vertical divider line
                const lineSprite: PIXI.Sprite = new PIXI.Sprite(box.backgroundTexture);
                lineSprite.anchor.set(0.5);

                this._boxWrapperSprite.addChild(lineSprite);
            }
            const contentWrapper: PIXI.Container = new PIXI.Container();

            if (box.infoSprite) {
                const info: PIXI.Sprite = box.infoSprite;
                info.anchor.set(0.5);

                contentWrapper.addChild(info);
            }

            if (box.title) {
                const title: PIXI.Text = new PIXI.Text(box.title, {});

                this._titles.push(title);
                contentWrapper.addChild(title);
            }

            if (box.text) {
                const text: PIXI.Text = new PIXI.Text(box.text, {
                    fill: 0x381600,
                    fontFamily: "Open Sans",
                    wordWrap: true,
                    padding: 20,
                    wordWrapWidth: 360,
                    fontSize: 30,
                    fontWeight: "bold",
                    breakWords: true
                });

                text.anchor.set(0.5);
                if (index === 2) { // wild raider screen
                    text.x = -200;
                }
                else {
                    text.x = 250;
                }

                this._paragraphs.push(text);
                contentWrapper.addChild(text);
            }

            // This is what controls which content is being shown
            this._pageSprites.push(contentWrapper);

            this._boxWrapperSprite.addChild(contentWrapper);

            if (index !== 0) {
                this._pageSprites[index].alpha = 0;
            }
        });
    }

    /**
     * Any non generic visual stuff could be created from this point
     */
    protected createFluff(): void {
        this.createRadioButtons("navDotOn", "navDotOff");
    }

    /**
     * Owls specific radio buttons
     */
    protected createRadioButtons(btnTextureName: string, btnFrameTextureName: string): void {
        const wrapperSprite: PIXI.Sprite = new PIXI.Sprite();
        wrapperSprite.anchor.set(0.5);

        this._pageSprites.forEach((sprite, index) => {
            const radioFrame: PIXI.Sprite = (btnFrameTextureName) ? new PIXI.Sprite(GameResources.getTextures(btnFrameTextureName)[0]) : new PIXI.Sprite();
            const radioBtn: PIXI.Sprite = (btnTextureName) ? new PIXI.Sprite(GameResources.getTextures(btnTextureName)[0]) : new PIXI.Sprite();

            radioBtn.anchor.set(0.5);
            radioFrame.anchor.set(0.5);
            radioFrame.x = (index * 40);

            radioFrame.addChild(radioBtn);

            this._radioButtons.push(radioBtn);

            wrapperSprite.addChild(radioFrame);

            if (index !== 0) {
                radioBtn.alpha = 0.1;
            }
        });

        wrapperSprite.x = -90;
        wrapperSprite.y = 265;

        wrapperSprite.scale.set(1.5);

        this._boxWrapperSprite.addChild(wrapperSprite);
        this._radioButtonWrapper = wrapperSprite;
    }

    protected initBaseInteractAbles(): void {
        if (this._arrowNextSprite) {
            this.initInteractive(this._arrowNextSprite, () => {
                this.slideShowEnabled = false;
                TweenMax.fromTo(this._arrowNextSprite.scale, 0.1, {
                    x: -1,
                    y: -1
                }, {
                    x: -0.9,
                    y: -0.9,
                    yoyo: true,
                    repeat: 1
                });
                this.toggleNextPage();
            });
        }

        if (this._arrowPrevSprite) {
            this.initInteractive(this._arrowPrevSprite, () => {
                this.slideShowEnabled = false;
                TweenMax.fromTo(this._arrowPrevSprite.scale, 0.1, {
                    x: 1,
                    y: 1
                }, {
                    x: 0.9,
                    y: 0.9,
                    yoyo: true,
                    repeat: 1
                });
                this.togglePreviousPage();
            });
        }
    }

    /**
     * Any non generic interactAbles could be handled from this point
     */
    protected initFluffInteractAbles(): void {
        this._radioButtons.forEach((sprite, index: number) => {
            if (index > -1) {
                this.initInteractive(sprite, () => {
                    if (index > -1) {
                        this.slideShowEnabled = false;
                        this.selectPage(index);
                    }
                })
            }
        });
    }

    protected visualSelectPage(fromPage: PIXI.Container, toPage: PIXI.Container) {
        if (this.getPageTransitionTimeline) {
            this.getPageTransitionTimeline(fromPage, toPage);
        } else {
            this._pageSprites.forEach((sprite) => {
                sprite.alpha = 0;
            });

            toPage.alpha = 1;
        }
    }

    private createPreviousButton(): void {
        if (this._arrowPrevSprite) {
            this._previousButton = this._arrowPrevSprite;
            this._previousButton.anchor.set(0.5);

            this._boxWrapperSprite.addChild(this._previousButton);
        }
    }

    private createNextButton(): void {
        if (this._arrowNextSprite) {
            this._nextButton = this._arrowNextSprite;
            this._nextButton.scale.set(-1);
            this._nextButton.anchor.set(0.5);

            this._boxWrapperSprite.addChild(this._nextButton);
        }
    }

    private initInteractive(sprite: PIXI.Sprite, clickCallback: (index?: number) => void = () => {}) {
        sprite.interactive = true;
        sprite.on('pointertap', clickCallback, this);
    }

    private toggleNextPage(): void {
        if (this._interactEnabled) {
            const nextIndex: number = this._currentIndex + 1 > this._pageSprites.length - 1 ? this._currentIndex = 0 : this._currentIndex + 1;
            this.selectPage(nextIndex);
        }
    }

    private togglePreviousPage(): void {
        if (this._interactEnabled) {
            const prevIndex: number = this._currentIndex - 1 < 0 ? this._currentIndex = this._pageSprites.length - 1 : this._currentIndex - 1;
            this.selectPage(prevIndex);
        }
    }

    private stopSlideShow(): void {
        if (this._slideShowTimeline && this._slideShowTimeline.isActive()) {
            this._slideShowTimeline.pause();
            this._slideShowTimeline.kill();
            this._slideShowRunning = false;
        }
    }

    private selectPage(pageIndex: number): void {
        if (this._interactEnabled) {
            if (pageIndex > -1 && pageIndex < this._pageSprites.length) {
                if (this._radioButtons) {
                    this._radioButtons.forEach((sprite) => {
                        sprite.alpha = 0.1;
                    });
                    this._radioButtons[pageIndex].alpha = 1;
                }

                this.visualSelectPage(this._pageSprites[this._currentIndex], this._pageSprites[pageIndex]);
                this._currentIndex = pageIndex;
            }
        }
    }

    public onResizeGameGraphics(resizeData: IResizeData): void {
        if (resizeData.orientation == Orientation.PORTRAIT) {
            this._boxWrapperSprite.scale.set(0.7);

            this._nextButton.x = 502;
            this._previousButton.x = -502;
        } else {
            this._boxWrapperSprite.scale.set(0.9);

            this._nextButton.x = 590;
            this._previousButton.x = -590;
        }
    }
}
